import {
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Tooltip,
  useToast,
  UseToastOptions,
  VStack,
} from '@chakra-ui/react';
import { useGetProjectsWithAmounts } from '~/common/api/orderingServiceApi/OrderingApi';
import { useGetCompanySummaryPdf } from '~/common/api/pdfServiceApi/PdfApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import AdjustFinishedProjectsModal from './AdjustFinishedProjectsModal';
import { usePersistedState } from '~/common/utils/localStorageUtils';
import { DatePickerInput } from 'chakra-datetime-picker';
import useTranslate from '~/common/utils/translate';
import { CloudDownload } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';

const Analysis: React.FC = () => {
  const [{ data: employees = [] }] = EmployeeApi.hooks.useEmployeeQuery();
  const [{ data: projects = [] }] = ProjectApi.hooks.useProjectsQuery();
  const [checkedEmployees, setCheckedEmployees] = usePersistedState(
    'checkedEmployees',
    [] as string[],
  );
  const t = useTranslate();
  const toast = useToast();

  const [startDate, setStartDate] = useState<Date>(
    new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1)),
  );
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [numberValue, setNumberValue] = useState<number | ''>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const {
    refetch: fetchCompanySummaryPdf,
    isFetching: isCompanySummaryPdfLoading,
  } = useGetCompanySummaryPdf({
    request: {
      startDate,
      endDate,
      additionalHours: numberValue !== '' ? Number(numberValue) : undefined,
      employeeIds: checkedEmployees.join(','),
    },
    enabled: false,
  });

  const showToast = (
    title: string,
    description: string,
    status: UseToastOptions['status'],
    duration: number,
  ): void => {
    toast({
      title,
      description,
      status,
      duration,
      position: 'top',
      variant: 'solid',
    });
  };

  const { data: projectsWithAmounts = [], refetch } = useGetProjectsWithAmounts(
    startDate,
    endDate,
  );

  const [pendingModal, setPendingModal] = useState(false);

  const handleDoneOrdersButtonClick = () => {
    refetch();
    setPendingModal(true);
  };

  useEffect(() => {
    if (pendingModal) {
      setIsModalOpen(true);
      setPendingModal(false);
    }
  }, [projectsWithAmounts, pendingModal]);

  const handleGetCompanySummary = async (): Promise<void> => {
    try {
      const { data } = await fetchCompanySummaryPdf();
      if (data) {
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const startstring = t('analysis.company.summary.pdf.title');
        a.download =
          startstring + `${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        showToast(
          t('analysis.company.summary.pdf.successToast.title')!,
          t('analysis.company.summary.pdf.successToast')!,
          'success',
          3000,
        );
      }
    } catch (error) {
      showToast(
        t('analysis.company.summary.pdf.errorToast.title')!,
        t('analysis.company.summary.pdf.errorToast')!,
        'error',
        5000,
      );
    }
  };

  return (
    <VStack spacing={8} align="stretch" mt={3}>
      <Heading size="2xl" textAlign="center" fontWeight="bold">
        Analysis
      </Heading>
      <Flex justifyContent="space-between" alignItems="flex-end" width="100%">
        <Flex flex={3} justifyContent="space-between">
          <Flex flex={1} mr={2} flexDirection="column">
            <Text mb={2}>Startzeitpunkt</Text>
            <DatePickerInput
              name="start-date"
              placeholder={t('analysis.company.summary.startDate')}
              value={startDate.toDateString()}
              onChange={(dateString: string) =>
                setStartDate(new Date(dateString))
              }
            />
          </Flex>
          <Flex flex={1} mx={2} flexDirection="column">
            <Text mb={2}>Endzeitpunkt</Text>
            <DatePickerInput
              name="end-date"
              placeholder={t('analysis.company.summary.endDate')}
              value={endDate.toDateString()}
              onChange={(dateString: string) =>
                setEndDate(new Date(dateString))
              }
            />
          </Flex>
          <Flex flex={1} ml={4} flexDirection="column">
            <Text mb={2}>{t('analysis.company.summary.additionalHours')}</Text>
            <NumberInput
              value={numberValue}
              onChange={(valueString) => setNumberValue(Number(valueString))}
              min={0}
              borderColor="black"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </Flex>
        </Flex>
        <Flex
          flex={1}
          mt={8}
          justifyContent="flex-end"
          alignItems="flex-end"
          mr={20}
        >
          <Tooltip
            label={
              t('analysis.company.summary.showDoneOrders.Tooltip') as string
            }
          >
            <Button onClick={handleDoneOrdersButtonClick} mr={2} size="lg">
              {t('analysis.company.summary.showDoneOrders.Button')}
            </Button>
          </Tooltip>
          <Tooltip
            label={t('analysis.company.summary.button.showPDF.text') as string}
          >
            <IconButton
              aria-label={
                t('analysis.company.summary.button.showPDF.text') as string
              }
              size="lg"
              icon={<CloudDownload />}
              onClick={() => {
                void handleGetCompanySummary();
              }}
              isLoading={isCompanySummaryPdfLoading}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <VStack align="stretch" spacing={4}>
        <Text>{t('analysis.company.summary.button.selectEmployee')}</Text>
        <Text fontWeight="bold"></Text>
        <CheckboxGroup
          colorScheme="blue"
          value={checkedEmployees}
          onChange={(values) => setCheckedEmployees(values as string[])}
        >
          <VStack align="stretch" spacing={2}>
            {employees
              .slice()
              .sort((a, b) => a.user.last_name.localeCompare(b.user.last_name))
              .map((employee) => (
                <Checkbox key={employee.id} value={employee.id.toString()}>
                  {employee.user.last_name}
                  {', '}
                  {employee.user.first_name}
                </Checkbox>
              ))}
          </VStack>
        </CheckboxGroup>
      </VStack>
      <AdjustFinishedProjectsModal
        isModalOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        allProjects={projects}
        projectsDurationSummaries={projectsWithAmounts}
      ></AdjustFinishedProjectsModal>
    </VStack>
  );
};

export default Analysis;
